import {ChangeDetectionStrategy, Component, computed, input} from '@angular/core';
import {MarkdownComponent, provideMarkdown} from 'ngx-markdown';
import {StaticTextBlock, staticTextFormat} from '../../shared/models/form-schema.model';

/**
 * One authored explanation, rendered under the field it explains in a submission read-back view (#501).
 *
 * A static-text block can name the fields it explains (`explains`), so the paragraph the author wrote next to a
 * question follows that question into the management detail page, the employee self-view and the PDF — instead
 * of being dropped, which is what both web views did before (they filtered every `static-text` block out).
 *
 * ### Why this is its own component
 * The runtime form (`forms-static-text`) renders the same two formats, but for a very different surface: there
 * the text is a full-width block of prose between the inputs, here it is a small muted footnote attached to one
 * value. Sharing one component would mean a mode flag on a presentational component and would couple the
 * read-back layout to the form runtime's spacing. What *is* shared is the schema helper
 * {@link staticTextFormat}, which is where the "absent format means `html`" default lives — so a legacy block
 * with no `format` renders here exactly as it always has.
 *
 * ### Sanitisation
 * Both branches sanitise. `[innerHTML]` runs Angular's own `DomSanitizer` in `SecurityContext.HTML`; the
 * Markdown branch goes through `ngx-markdown`, whose `MarkdownService` sanitises its output with the same
 * sanitiser in the same context (`provideMarkdown()` defaults `SANITIZE` to `SecurityContext.HTML`). Form
 * definitions are authored by company admins rather than by employees, but the content still reaches every
 * reader of a submission, so neither branch is allowed to opt out.
 *
 * `provideMarkdown()` is declared on **this component** rather than in the app's root providers on purpose: it
 * returns a plain `Provider[]`, so a component-level declaration makes the note self-contained — it works in a
 * TestBed spec, in a Storybook story and in the app without any bootstrap change, and without racing whatever
 * the form runtime decides to do about its own Markdown support.
 */
@Component({
  selector: 'forms-field-note',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MarkdownComponent],
  providers: [provideMarkdown()],
  template: `
    @if (isMarkdown()) {
      <markdown class="block prose prose-sm max-w-none text-xs leading-relaxed text-gray-500 marker:text-gray-400"
                [data]="note().content"></markdown>
    } @else {
      <div class="block prose prose-sm max-w-none text-xs leading-relaxed text-gray-500 marker:text-gray-400"
           [innerHTML]="note().content"></div>
    }
  `
})
export class FieldNoteComponent {
  /** The static-text block to render. Its `format` decides the branch; absent means `html` (legacy default). */
  readonly note = input.required<StaticTextBlock>();

  /** Whether this note is authored as Markdown (`html` otherwise — including when the block declares nothing). */
  protected readonly isMarkdown = computed(() => staticTextFormat(this.note()) === 'markdown');
}
